import React, { useState, useEffect } from 'react';
import { supabase } from '../../utils/supabaseClient';
import { Search, Ban, CheckCircle, ShieldAlert, FileText } from 'lucide-react';
import { AvatarDisplay } from '../../utils/avatars';
import UserAuditModal from './UserAuditModal';
import { useModal } from '../../context/ModalContext';

export default function AdminUsers() {
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all'); // all | incomplete
  const { showConfirm, showAlert } = useModal();

  const incompleteCount = users.filter(u => u.is_onboarded === false).length;
  const visibleUsers = statusFilter === 'incomplete'
    ? users.filter(u => u.is_onboarded === false)
    : users;

  const fetchRecentUsers = async () => {
    setIsLoading(true);
    try {
      // email 已自 profiles 移除，改由 admin_list_users RPC（內含 admin 權限檢查）取得
      const { data, error } = await supabase.rpc('admin_list_users');

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Fetch recent users error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRecentUsers();
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) {
      fetchRecentUsers();
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('admin_list_users', { search: searchQuery });

      if (error) throw error;
      setUsers(data || []);
    } catch (err) {
      console.error('Search users error:', err);
      showAlert('錯誤', '搜尋失敗');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleBan = async (userId, currentBanStatus, userName) => {
    const actionName = currentBanStatus ? '解除停權' : '停權';
    showConfirm('用戶停權管理', `確定要將用戶「${userName}」${actionName}嗎？`, async () => {
      try {
        const { error } = await supabase.rpc('admin_ban_user', {
          target_user_id: userId,
          ban_status: !currentBanStatus
        });

        if (error) throw error;

        setUsers(prev => prev.map(u => 
          u.id === userId ? { ...u, is_banned: !currentBanStatus } : u
        ));
        
        showAlert('成功', `已成功${actionName}該用戶`);
      } catch (err) {
        console.error('Toggle ban error:', err);
        showAlert('錯誤', '操作失敗，請確認您是否有管理員權限');
      }
    });
  };

  return (
    <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '1.25rem', color: '#1e293b', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <ShieldAlert color="#6a9fd3" />
          用戶狀態管理
        </h2>
        
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '12px' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} size={20} />
            <input
              type="text"
              placeholder="輸入姓名或 Email 搜尋用戶..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: '100%',
                padding: '12px 12px 12px 40px',
                borderRadius: '8px',
                border: '1px solid #e2e8f0',
                outline: 'none',
                fontSize: '15px'
              }}
            />
          </div>
          <button 
            type="submit"
            disabled={isLoading}
            style={{
              padding: '0 24px',
              background: '#4e8bca',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontWeight: 'bold',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              opacity: isLoading ? 0.7 : 1
            }}
          >
            {isLoading ? '搜尋中...' : '搜尋'}
          </button>
        </form>

        {/* 狀態篩選分頁 */}
        <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
          {[
            { key: 'all', label: `全部 (${users.length})` },
            { key: 'incomplete', label: `註冊未完成 (${incompleteCount})` }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setStatusFilter(tab.key)}
              style={{
                padding: '8px 16px',
                borderRadius: '20px',
                border: statusFilter === tab.key ? 'none' : '1px solid #e2e8f0',
                background: statusFilter === tab.key ? '#4e8bca' : 'white',
                color: statusFilter === tab.key ? 'white' : '#64748b',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        {visibleUsers.length > 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {visibleUsers.map(user => (
              <div key={user.id} style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px',
                border: '1px solid #f1f5f9',
                borderRadius: '12px',
                background: user.is_banned ? '#fef2f2' : 'white',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  <AvatarDisplay avatarId={user.avatar || 'avatar1'} size={48} />
                  <div>
                    <h3 style={{ margin: '0 0 4px', fontSize: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {user.name}
                      {user.role === 'admin' && (
                        <span style={{ fontSize: '10px', background: '#6a9fd3', color: 'white', padding: '2px 6px', borderRadius: '12px' }}>Admin</span>
                      )}
                      {user.is_banned && (
                        <span style={{ fontSize: '10px', background: '#ef4444', color: 'white', padding: '2px 6px', borderRadius: '12px' }}>Banned</span>
                      )}
                      {user.is_onboarded === false && (
                        <span style={{ fontSize: '10px', background: '#fef3c7', color: '#b45309', border: '1px solid #fbbf24', padding: '2px 6px', borderRadius: '12px' }}>註冊未完成</span>
                      )}
                    </h3>
                    <p style={{ margin: 0, fontSize: '13px', color: '#64748b' }}>{user.email}</p>
                    <p style={{ margin: '4px 0 0', fontSize: '12px', color: '#94a3b8' }}>
                      註冊於 {new Date(user.created_at).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  {user.is_banned ? (
                    <button
                      onClick={() => handleToggleBan(user.id, user.is_banned, user.name)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', background: 'white', border: '1px solid #10b981', color: '#10b981', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                      }}
                    >
                      <CheckCircle size={16} /> 解除停權
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleBan(user.id, user.is_banned, user.name)}
                      disabled={user.role === 'admin'}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '6px',
                        padding: '8px 16px', background: '#ef4444', border: 'none', color: 'white', borderRadius: '8px', cursor: user.role === 'admin' ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: user.role === 'admin' ? 0.5 : 1
                      }}
                    >
                      <Ban size={16} /> 停權
                    </button>
                  )}
                  
                  <button
                    onClick={() => setSelectedUser(user)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '6px',
                      padding: '8px 16px', background: '#f1f5f9', border: 'none', color: '#475569', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold'
                    }}
                  >
                    <FileText size={16} /> 查看詳情
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#94a3b8' }}>
            <Users size={48} style={{ opacity: 0.2, margin: '0 auto 16px' }} />
            <p>{isLoading ? '載入中...' : '沒有找到符合的用戶'}</p>
          </div>
        )}
      </div>

      {selectedUser && (
        <UserAuditModal user={selectedUser} onClose={() => setSelectedUser(null)} />
      )}
    </div>
  );
}

// Just add this helper icon if lucide-react hasn't exported Users
const Users = ({ size, style }) => (
  <svg width={size} height={size} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
    <circle cx="9" cy="7" r="4"></circle>
    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
  </svg>
);
